/* -----------------Globals--------------- */
import { SectionList } from "react-native";

/* -----------------UI--------------- */
import Box from "@memorang/ui/components/Box";
import { PremiumChip } from "@memorang/ui/components/PremiumChip";

/* -----------------Types--------------- */
import type { PracticeTestsViewSection } from "../helpers/practice-tests-data";

import { getIsOlxExam, isOlxExamWithinDateRange } from "@helpers/exams";
/* -----------------Child components--------------- */
import { useAppStore } from "@hooks/useAppStore";
import type { ExamStatus } from "@memorang/types";
import { PracticeTestListItem } from "@memorang/ui/components/PracticeTestListItem";
import { useAppTheme } from "@memorang/ui/useAppTheme";
import { useDevice } from "@memorang/ui/useDevice";
import CustomListSubheaderContainer from "components/CustomListSubheaderContainer";

export const PracticeTestsView = ({
	sections,
	selectedExamId,
	handleNavigation,
	hasProductAccess,
	canBypassPaywall,
}: {
	sections: PracticeTestsViewSection[];
	selectedExamId?: string;
	disableCard?: boolean;
	hasProductAccess?: boolean;
	canBypassPaywall?: boolean;
	handleNavigation: (
		id: string,
		status: ExamStatus,
		numericId: number | undefined,
		isQuiz?: boolean,
		hasExamAccess?: boolean,
		canStartSession?: boolean,
		collectionId?: number,
	) => void;
}) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();
	const scoreRanges = useAppStore((state) => state.app?.scoreRanges);

	const renderSectionHeader = ({
		section: { title, rightChip },
	}: {
		section: PracticeTestsViewSection;
	}) => {
		return (
			<Box
				gap={16}
				backgroundColor={theme.colors.background}
				paddingHorizontal={isMobile ? 16 : 0}
			>
				<Box
					justifyContent="space-between"
					flexDirection="row"
					alignItems="center"
				>
					<CustomListSubheaderContainer enableGutters={false}>
						{title}
					</CustomListSubheaderContainer>
					{rightChip && <PremiumChip {...rightChip} />}
				</Box>
			</Box>
		);
	};

	const renderItem = ({
		item,
	}: {
		item: PracticeTestsViewSection["data"][0];
	}) => {
		return (
			<Box gap={isMobile ? 0 : 16}>
				{item.list[0].map((practiceTest) => {
					const {
						title,
						chips,
						completionRing,
						status,
						cta,
						numericId,
						collectionId,
						id,
						score,
						numPointsEarned,
						numPointsTotal,
						hasExamAccess,
						beginStartDate,
						endStartDate,
						subType,
						legacyReport,
						migratedSessionReport,
						scoreText: highlightedTopicScoreText,
					} = practiceTest;
					const scoreText =
						highlightedTopicScoreText ??
						(numPointsTotal
							? `${Math.round(numPointsEarned ?? 0)}`
							: undefined);
					const isFreeExam = subType === "DIAGNOSTIC";

					const isOlxExam = getIsOlxExam(id);

					const getCanStartSession = () => {
						if (isOlxExam) {
							if (status === "ACTIVE" || status === "COMPLETE") {
								return true;
							}
							return isOlxExamWithinDateRange(beginStartDate, endStartDate);
						}
						return true;
					};

					const canStartSession = getCanStartSession();
					const locked = !(
						(hasExamAccess && hasProductAccess) ||
						isFreeExam ||
						canBypassPaywall ||
						legacyReport ||
						migratedSessionReport
					);

					return (
						<PracticeTestListItem
							key={id}
							label={title}
							chips={chips}
							completionRing={locked ? undefined : completionRing}
							icon={locked ? "lock-outline" : undefined}
							status={status}
							cta={cta}
							locked={locked}
							loading={selectedExamId === id}
							score={score}
							scoreText={scoreText}
							scoreRanges={scoreRanges}
							handleNavigation={() =>
								handleNavigation(
									id,
									status,
									numericId,
									isFreeExam,
									hasExamAccess,
									canStartSession,
									collectionId,
								)
							}
						/>
					);
				})}
			</Box>
		);
	};

	return (
		<SectionList
			sections={sections}
			showsVerticalScrollIndicator={false}
			keyExtractor={(_, index) => index.toString()}
			renderItem={renderItem}
			renderSectionHeader={renderSectionHeader}
			SectionSeparatorComponent={() => <Box height={8} />}
		/>
	);
};
